/**
 * COPY STRATEGY — AdvancedExpenseStrategyPage
 *
 * Awareness:      Solution-Aware (Stage 3/5)
 *                 Reader already knows about actual expenses, learning an
 *                 advanced optimization strategy to stay in 5% GPM zone.
 *
 * Sophistication: Stage 4 — Enlarged Mechanism
 *                 Advanced version of the expense strategy from ExpenseNormPage.
 *                 "60% expenses" is the enlarged mechanism — not just "use actual
 *                 expenses" but a specific ratio that unlocks 5% GPM at €45k income.
 *
 * Avatar:         High-earning driver, €3000+/month revenue.
 *                 Already uses actual expenses, wants to push further.
 *                 Willing to handle FR0457, fuel cards, detailed documentation.
 *                 Ambitious, sees driving as a serious business.
 *
 * Hook:           Aspirational — "€45 000 ir mokėti tik 5% GPM"
 * Structure:      Strategy reveal → Before/after comparison → Requirements → CTA
 * Proof:          Side-by-side 30% vs 60% tax calculation at €45k income
 * Funnel:         Feeds from ExpenseNormPage, leads to FR0457 and fuel accounting
 */
import { motion } from 'framer-motion'
import { PiWarning, PiReceipt } from 'react-icons/pi'

import HeaderBack from '@/components/dashboard/Header/HeaderBack'
import MotionText from '@/components/common/Motion/MotionText'
import MotionDiv from '@/components/common/Motion/MotionDiv'
import ButtonLink from '@components/common/Button/ButtonLink'
import FinhudasCTA from '@/components/common/Blog/FinhudasCTA'
import RelatedArticles from '@/components/common/Blog/RelatedArticles'
import { usePageSetup } from '@/hooks/usePageSetup'

const AdvancedExpenseStrategyPage = () => {
  usePageSetup('Kaip uždirbti €45 000 ir mokėti tik 5% GPM - Finhudas', {
    scroll: true,
    description: 'Pavežėjų išlaidos dažnai siekia 60% pajamų. Tai leidžia uždirbti €45 000 ir mokėti tik 5% GPM vietoj 12,7%. Strategija su konkrečiais skaičiavimais ir žingsniais.',
    type: 'article',
    datePublished: '2025-11-28',
  })

  return (
    <>
      <HeaderBack to="/blog" />
      <div className="min-h-full bg-white px-5 pb-8 flex flex-col">

        <MotionText
          as="h1"
          className="text-zinc-900 text-2xl font-medium mt-3 mb-2"
        >
          60% faktinės išlaidos: kaip uždirbti €45 000 ir mokėti tik 5% GPM
        </MotionText>

        <MotionDiv className="flex items-center gap-2 mb-6">
          <span className="text-xs font-medium text-zinc-500">Mokesčiai</span>
          <span className="text-xs text-zinc-300">•</span>
          <span className="text-xs text-zinc-500">8 min skaitymo</span>
        </MotionDiv>

        <MotionText
          as="p"
          className="text-zinc-700 text-base mb-6"
        >
          Daugelis pavežėjų nežino, kad galima uždirbti €45 000 per metus ir vis tiek mokėti tik 5% GPM. Paslaptis – faktinių išlaidų deklaravimas. Šiame straipsnyje sužinosite, kaip tai padaryti legaliai ir saugiai.
        </MotionText>

        <MotionDiv className="p-4 bg-green-50 border-l-4 border-green-500 rounded-r-xl mb-4">
          <MotionText as="h3" className="text-green-900 text-base font-medium mb-3">
            💰 Pagrindinė strategija
          </MotionText>
          <MotionText as="p" className="text-green-800 text-sm mb-3">
            Pavežėjų išlaidos dažnai siekia 60% pajamų (komisinis ~25% + kuras + remontas). Tai leidžia išlikti 5% mokesčio zonoje net su aukštomis pajamomis:
          </MotionText>

          <MotionDiv className="grid grid-cols-2 gap-3 mb-3">
            <MotionDiv className="bg-red-50 border border-red-200 p-3 rounded-lg">
              <MotionText as="p" className="text-red-900 text-xs font-medium mb-2">
                ❌ Su 30% norma:
              </MotionText>
              <MotionText as="p" className="text-red-800 text-xs mb-1">
                Pajamos: 45 000 €
              </MotionText>
              <MotionText as="p" className="text-red-800 text-xs mb-1">
                Išlaidos: 13 500 € (30%)
              </MotionText>
              <MotionText as="p" className="text-red-800 text-xs mb-1">
                Pelnas: 31 500 €
              </MotionText>
              <MotionText as="p" className="text-red-900 text-xs font-medium">
                Tarifas: 12,7% (virš 20k)
              </MotionText>
            </MotionDiv>

            <MotionDiv className="bg-green-100 border border-green-300 p-3 rounded-lg">
              <MotionText as="p" className="text-green-900 text-xs font-medium mb-2">
                ✅ Su 60% faktinėmis:
              </MotionText>
              <MotionText as="p" className="text-green-800 text-xs mb-1">
                Pajamos: 45 000 €
              </MotionText>
              <MotionText as="p" className="text-green-800 text-xs mb-1">
                Išlaidos: 27 000 € (60%)
              </MotionText>
              <MotionText as="p" className="text-green-800 text-xs mb-1">
                Pelnas: 18 000 €
              </MotionText>
              <MotionText as="p" className="text-green-900 text-xs font-medium">
                Tarifas: 5% (iki 20k)
              </MotionText>
            </MotionDiv>
          </MotionDiv>

          <MotionText as="p" className="text-green-700 text-xs">
            Rezultatas: Liekate žemiausio mokesčio zonoje, net uždirbus 2x daugiau nei su 30% norma!
          </MotionText>
        </MotionDiv>

        <MotionText
          as="h2"
          className="text-zinc-900 text-xl font-medium border-t border-zinc-200 mt-6 pt-6 mb-2"
        >
          Kodėl tai veikia?
        </MotionText>

        <MotionDiv className="p-4 bg-blue-50 border border-blue-200 rounded-xl mb-4">
          <MotionText as="p" className="text-blue-800 text-sm mb-3">
            Atminkite: 5% mokesčio tarifas taikomas <strong>apmokestinamajam pelnui</strong>, o ne bendrosioms pajamoms.
          </MotionText>
          <MotionText as="p" className="text-blue-800 text-sm mb-3">
            20 000 € pelno riba galioja <strong>po išlaidų atskaitymų</strong>. Tai reiškia:
          </MotionText>
          <ul className="space-y-2">
            <li className="text-blue-700 text-sm">
              • Su 30% norma: galite uždirbti iki 28 571 € ir likti 5% zonoje
            </li>
            <li className="text-blue-700 text-sm">
              • Su 60% faktinėmis: galite uždirbti iki 50 000 € ir likti 5% zonoje
            </li>
          </ul>
        </MotionDiv>

        <MotionText
          as="h2"
          className="text-zinc-900 text-xl font-medium border-t border-zinc-200 mt-6 pt-6 mb-2"
        >
          Ką sudaro faktinės išlaidos pavežėjui?
        </MotionText>

        <MotionDiv className="space-y-3 mb-8">
          <MotionDiv className="p-3 bg-zinc-50 rounded-lg">
            <MotionText as="p" className="text-zinc-900 text-sm font-medium mb-1">
              1. Platformos komisinis (20-25%)
            </MotionText>
            <MotionText as="p" className="text-zinc-700 text-xs mb-1">
              Didžiausia dalis išlaidų. Bolt/Uber/Wolt moka jums į rankas tik 75-80% keleivių mokėjimų.
            </MotionText>
            <MotionText as="p" className="text-zinc-600 text-xs">
              📄 Dokumentas: Mėnesinė sąskaita iš platformos portalo
            </MotionText>
          </MotionDiv>

          <MotionDiv className="p-3 bg-zinc-50 rounded-lg">
            <MotionText as="p" className="text-zinc-900 text-sm font-medium mb-1">
              2. Kuras
            </MotionText>
            <MotionText as="p" className="text-zinc-700 text-xs mb-1">
              Paprastas degalinės čekis be rekvizitų NETINKA. Reikia, kad dokumente būtų jūsų vardas, pavardė ir asmens kodas (arba IV numeris).
            </MotionText>
            <MotionText as="p" className="text-zinc-600 text-xs">
              ✅ Sprendimas: Circle K / Neste verslo kortelė – gausite SF su rekvizitais
            </MotionText>
          </MotionDiv>

          <MotionDiv className="p-3 bg-zinc-50 rounded-lg">
            <MotionText as="p" className="text-zinc-900 text-sm font-medium mb-1">
              3. Automobilio remontas ir dalys
            </MotionText>
            <MotionText as="p" className="text-zinc-700 text-xs mb-1">
              Padangos, tepalai, gedimų taisymas.
            </MotionText>
            <MotionText as="p" className="text-zinc-600 text-xs">
              📄 SF su jūsų vardu ir IV numeriu
            </MotionText>
          </MotionDiv>

          <MotionDiv className="p-3 bg-zinc-50 rounded-lg">
            <MotionText as="p" className="text-zinc-900 text-sm font-medium mb-1">
              4. Draudimas (KASKO + OCTA)
            </MotionText>
            <MotionText as="p" className="text-zinc-700 text-xs">
              Privalomas OCTA ir savanoriškas KASKO. Abi sąskaitos atskaičiuojamos.
            </MotionText>
          </MotionDiv>

          <MotionDiv className="p-3 bg-zinc-50 rounded-lg">
            <MotionText as="p" className="text-zinc-900 text-sm font-medium mb-1">
              5. Automobilio nusidėvėjimas
            </MotionText>
            <MotionText as="p" className="text-zinc-700 text-xs">
              Jei įsigijote naują/naudotą auto, galite per kelerius metus atskaityti jo įsigijimo kainą.
            </MotionText>
          </MotionDiv>

          <MotionDiv className="p-3 bg-zinc-50 rounded-lg">
            <MotionText as="p" className="text-zinc-900 text-sm font-medium mb-1">
              6. Mobilus ryšys, apskaita
            </MotionText>
            <MotionText as="p" className="text-zinc-700 text-xs">
              Telefono planas, internetas, apskaitos programos ar buhalterio paslaugos.
            </MotionText>
          </MotionDiv>
        </MotionDiv>

        <MotionText
          as="h2"
          className="text-zinc-900 text-xl font-medium border-t border-zinc-200 mt-6 pt-6 mb-2"
        >
          ⚠️ Privalomi žingsniai
        </MotionText>

        <MotionText as="p" className="text-zinc-700 text-sm mb-4">
          Negalite tiesiog įrašyti "60%" į deklaraciją. VMI reikalauja griežtų dokumentų ir automobilio deklaravimo:
        </MotionText>

        <MotionDiv className="space-y-4 mb-8">
          <MotionDiv className="p-4 bg-red-50 border-l-4 border-red-500 rounded-r-xl">
            <div className="flex gap-3">
              <PiWarning className="min-w-6 h-6 text-red-700" />
              <div>
                <MotionText as="h3" className="text-red-900 text-sm font-medium mb-2">
                  1. FR0457 forma (automobilio deklaravimas)
                </MotionText>
                <MotionText as="p" className="text-red-800 text-sm mb-2">
                  <strong>Kas tai?</strong> Oficiali forma, kuria deklaruojate automobilį kaip verslo turtą VMI.
                </MotionText>
                <MotionText as="p" className="text-red-700 text-sm mb-2">
                  <strong>Kaip?</strong> Per "Mano VMI" elektroninę sistemą.
                </MotionText>
                <MotionText as="p" className="text-red-700 text-sm mb-3">
                  <strong>Svarbu:</strong> Deklaruokite 90-95% verslo naudojimui, ne 100%. VMI supranta, kad auto naudojate ir asmeniniams tikslams (parduotuvė, šeima).
                </MotionText>
                <MotionDiv className="p-3 bg-red-100 rounded-lg">
                  <MotionText as="p" className="text-red-900 text-xs font-medium mb-1">
                    ⚡ Rizika:
                  </MotionText>
                  <MotionText as="p" className="text-red-800 text-xs">
                    Jei vėliau parduosite automobilį, gali tekti mokėti mokestį nuo pardavimo kainos (kapitalo prieaugio mokestis).
                  </MotionText>
                </MotionDiv>
              </div>
            </div>
          </MotionDiv>

          <MotionDiv className="p-4 bg-amber-50 border-l-4 border-amber-500 rounded-r-xl">
            <div className="flex gap-3">
              <PiReceipt className="min-w-6 h-6 text-amber-700" />
              <div>
                <MotionText as="h3" className="text-amber-900 text-sm font-medium mb-2">
                  2. Dokumentai su pirkėjo rekvizitais
                </MotionText>
                <MotionText as="p" className="text-amber-800 text-sm mb-3">
                  <strong>Svarbu:</strong> Čekis ar SF turi turėti pirkėjo duomenis – vardą, pavardę ir asmens kodą (arba IV numerį). Paprastas čekis be šių duomenų netinka!
                </MotionText>

                <MotionText as="p" className="text-amber-900 text-sm font-medium mb-1">
                  Sprendimas:
                </MotionText>
                <ul className="space-y-2">
                  <li className="text-amber-800 text-sm">
                    • <strong>Circle K:</strong> "Mileage" verslo kortelė
                  </li>
                  <li className="text-amber-800 text-sm">
                    • <strong>Neste:</strong> "Neste Pro" verslo kortelė
                  </li>
                  <li className="text-amber-800 text-sm">
                    • <strong>Viada:</strong> Verslo klientų programa
                  </li>
                </ul>
                <MotionText as="p" className="text-amber-700 text-xs mt-2">
                  Su šiomis kortelėmis gausite mėnesines SF su jūsų asmens kodu. Kiekvieno mėnesio pabaigoje – viena SF su visu kiekiu.
                </MotionText>
              </div>
            </div>
          </MotionDiv>

        </MotionDiv>

        <MotionText
          as="h2"
          className="text-zinc-900 text-xl font-medium border-t border-zinc-200 mt-6 pt-6 mb-2"
        >
          Žingsnis po žingsnio: kaip pradėti
        </MotionText>

        <MotionDiv className="space-y-3 mb-8">
          <MotionDiv className="p-4 border-l-4 border-zinc-300 bg-zinc-50 rounded-r-xl">
            <MotionText as="p" className="text-zinc-900 text-sm font-medium mb-1">
              1 žingsnis: Apskaičiuokite faktines išlaidas
            </MotionText>
            <MotionText as="p" className="text-zinc-700 text-sm">
              Bent 2-3 mėnesius rinkite visus kvitus. Suskaičiuokite: ar išlaidos tikrai viršija 30%? Jei ne – neverta komplikuoti.
            </MotionText>
          </MotionDiv>

          <MotionDiv className="p-4 border-l-4 border-zinc-300 bg-zinc-50 rounded-r-xl">
            <MotionText as="p" className="text-zinc-900 text-sm font-medium mb-1">
              2 žingsnis: Gaukite verslo kortelę degalams
            </MotionText>
            <MotionText as="p" className="text-zinc-700 text-sm">
              Eikite į Circle K ar Neste ir užsisakykite verslo kortelę su jūsų asmens kodu. Tai užtruks ~1 savaitę.
            </MotionText>
          </MotionDiv>

          <MotionDiv className="p-4 border-l-4 border-zinc-300 bg-zinc-50 rounded-r-xl">
            <MotionText as="p" className="text-zinc-900 text-sm font-medium mb-1">
              3 žingsnis: Pateikite FR0457 formą
            </MotionText>
            <MotionText as="p" className="text-zinc-700 text-sm">
              Per "Mano VMI" → "Turto deklaravimas" → FR0457. Nurodykite automobilį (90-95% verslo naudojimas).
            </MotionText>
          </MotionDiv>

          <MotionDiv className="p-4 border-l-4 border-zinc-300 bg-zinc-50 rounded-r-xl">
            <MotionText as="p" className="text-zinc-900 text-sm font-medium mb-1">
              4 žingsnis: Tvarkykite pajamų ir išlaidų apskaitą su Finhudas
            </MotionText>
            <MotionText as="p" className="text-zinc-700 text-sm">
              Įkelkite Bolt/Uber balansą ir išlaidų dokumentus į Finhudas – sistema susumuos visas pajamas ir išlaidas, pateiks ataskaitas metinei deklaracijai.
            </MotionText>
          </MotionDiv>

          <MotionDiv className="p-4 border-l-4 border-zinc-300 bg-zinc-50 rounded-r-xl">
            <MotionText as="p" className="text-zinc-900 text-sm font-medium mb-1">
              5 žingsnis: Gegužės mėn. pateikite GPM311
            </MotionText>
            <MotionText as="p" className="text-zinc-700 text-sm">
              Metinėje deklaracijoje nurodykite faktines išlaidas su visomis SF. GPM bus skaičiuojamas nuo tikrojo pelno.
            </MotionText>
          </MotionDiv>
        </MotionDiv>

        <MotionDiv className="p-4 bg-green-50 border border-green-200 rounded-xl mb-4">
          <MotionText as="h3" className="text-green-900 text-sm font-medium mb-2">
            ✅ Kam tikrai verta?
          </MotionText>
          <ul className="space-y-2">
            <li className="text-green-800 text-sm">
              • Uždirbate {'>'} €30 000/metus ir norite išlikti 5% zonoje
            </li>
            <li className="text-green-800 text-sm">
              • Turite daug kuro ir remonto išlaidų (faktiškai {'>'} 50%)
            </li>
            <li className="text-green-800 text-sm">
              • Nesunku gauti verslo kortelę ir tvarkyti dokumentus
            </li>
            <li className="text-green-800 text-sm">
              • Planuojate dirbti pavežėju ilgai (ne trumpalaikis)
            </li>
          </ul>
        </MotionDiv>

        <MotionDiv className="p-4 bg-amber-50 border border-amber-200 rounded-xl mb-4">
          <MotionText as="h3" className="text-amber-900 text-sm font-medium mb-2">
            ⚠️ Kam NEVERTA?
          </MotionText>
          <ul className="space-y-2">
            <li className="text-amber-800 text-sm">
              • Uždirbate {'<'} €25 000/metus (ir taip mokėsite 5% su 30% norma)
            </li>
            <li className="text-amber-800 text-sm">
              • Nenorite komplikuoti apskaitos
            </li>
            <li className="text-amber-800 text-sm">
              • Vairuojate elektromobilį (mažos kuro išlaidos)
            </li>
            <li className="text-amber-800 text-sm">
              • Planuojate greitai parduoti automobilį
            </li>
          </ul>
        </MotionDiv>

        <MotionText
          as="h2"
          className="text-zinc-900 text-xl font-medium border-t border-zinc-200 mt-6 pt-6 mb-2"
        >
          Teisiniai pagrindai
        </MotionText>

        <MotionDiv className="p-4 bg-blue-50 border border-blue-200 rounded-xl mb-4">
          <MotionText as="p" className="text-blue-800 text-sm mb-2">
            <strong>Išlaidų atskaitymui:</strong> Lietuvos Respublikos gyventojų pajamų mokesčio įstatymas (GPMĮ), 18 straipsnis
          </MotionText>
          <MotionText as="p" className="text-blue-700 text-xs">
            Leidžiamos išlaidos, tiesiogiai susijusios su individualios veiklos vykdymu ir pajamų uždirbmu.
          </MotionText>
        </MotionDiv>

        <FinhudasCTA awareness="solution" />

        <RelatedArticles articles={[
          { slug: 'fr0457-automobilio-deklaravimas-vmi', title: 'Kaip užpildyti FR0457 formą: žingsnis po žingsnio' },
          { slug: 'kuro-apskaita-3-metodai-islaidos', title: 'Kuro išlaidos: 3 metodai ir dokumentų reikalavimai' },
          { slug: 'individualios-veiklos-islaidos-30-procentu-arba-faktines', title: '30% norma ar faktinės išlaidos?' }
        ]} />

        <motion.div
          className="mt-10"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.3 }}
        >
          <ButtonLink variant="secondary" to="/blog">
            Grįžti į straipsnius
          </ButtonLink>
        </motion.div>
      </div>
    </>
  )
}

export default AdvancedExpenseStrategyPage
